import React, { useEffect, useMemo, useState } from "react";
import { StyledInput } from "./styles";
import { StyledNumberInlineInput } from "../common.styles";
import { toLocaleNumber, toRawNumber } from "../../../../utils/translation";
import {
  handleNumberInputKeyDown,
  handleNumberInputPaste,
} from "../../../../utils/common";

type Props = {
  id?: string;
  value: string | number;
  onChange?: (value: number) => void;
  maxLimit?: number;
  disabled?: boolean;
  minLimit?: number;
  displayStyle?: string;
  width?: string;
  inline?: boolean;
  lang: string;
};

const NumberInput: React.FC<Props> = ({
  id,
  value,
  onChange,
  maxLimit = undefined,
  disabled = false,
  minLimit = 0,
  displayStyle = "block",
  width,
  inline = false,
  lang = "English",
}) => {
  const [data, setData] = useState(value?.toString() || "");
  const ref = React.useRef<HTMLInputElement>(null);
  useEffect(() => {
    setData(value?.toString() || "");
  }, [value]);
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value === "") {
      setData("");
      onChange?.(0);
      return;
    }
    if (value) {
      const rawNumber = toRawNumber(value, lang);
      const parsedValue = parseFloat(rawNumber);
      const v =
        maxLimit && parsedValue > maxLimit
          ? maxLimit
          : parsedValue < minLimit
          ? 0
          : rawNumber;

      if (maxLimit && parsedValue > maxLimit && ref.current) {
        ref.current.value = maxLimit.toString();
      }
      if (minLimit > parsedValue && ref.current) {
        ref.current.value = minLimit.toString();
      }
      const preciseVal: string = Number(v).toFixed(2);
      setData(v.toString());
      onChange?.(preciseVal === "NaN" ? 0 : Number(preciseVal));
    }
  };
  const handleBlur = () => {
    if (data === "") return onChange?.(0);
    const fixedValue: string = Number(data).toFixed(2);
    setData(Number(fixedValue).toString());
    onChange?.(Number(fixedValue));
  };

  const displayData = useMemo(() => {
    const val = toLocaleNumber(data, lang);
    return val === "NaN" ? "" : val;
  }, []);
  if (inline) {
    return (
      <StyledNumberInlineInput
        id={id}
        ref={ref}
        defaultValue={displayData}
        onChange={handleChange}
        onKeyDown={(e) => handleNumberInputKeyDown(e, lang)}
        onPaste={(e) => handleNumberInputPaste(e, lang)}
        onBlur={handleBlur}
        disabled={disabled}
        type="text"
      />
    );
  }
  return (
    <StyledInput
      id={id}
      ref={ref}
      defaultValue={displayData}
      style={{
        display: displayStyle,
        cursor: disabled ? "not-allowed" : "",
        backgroundColor: disabled ? "#f5f5f5" : "#fff",
      }}
      onKeyDown={(e) => handleNumberInputKeyDown(e, lang)}
      onPaste={(e) => handleNumberInputPaste(e, lang)}
      width={width}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
      type="text"
    />
  );
};

export { NumberInput };
